import { gapi } from 'gapi-script';
import LocationsData from '@/consts/dataforseo_locations.json';
import moment from 'moment';
import { getCountryData } from 'countries-list'

const { locations } = LocationsData


export const GOOGLE_OAUTH_SCOPE_DEF = 'https://www.googleapis.com/auth/webmasters https://www.googleapis.com/auth/webmasters.readonly'

// Scope the backend-token (GIS authorization-code) flow requests: GSC read
// access plus identity so the UI can show which account is connected.
export const GOOGLE_CODE_FLOW_SCOPE = 'https://www.googleapis.com/auth/webmasters.readonly openid email profile'

const GSI_SRC = 'https://accounts.google.com/gsi/client'
let gsiScriptPromise = null

function loadGsiScript () {
  if (window.google && window.google.accounts && window.google.accounts.oauth2) {
    return Promise.resolve()
  }
  if (!gsiScriptPromise) {
    gsiScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.src = GSI_SRC
      script.async = true
      script.onload = resolve
      script.onerror = () => {
        gsiScriptPromise = null
        reject(new Error('Failed to load the Google sign-in script'))
      }
      document.head.appendChild(script)
    })
  }
  return gsiScriptPromise
}

/**
 * Google Identity Services authorization-code popup. Resolves the one-time
 * code the backend exchanges for a refresh token (connectGoogleAccount).
 * Rejects with error.popupClosed = true when the user just closes the popup.
 */
export const requestGoogleAuthCode = () => {
  return loadGsiScript().then(() => new Promise((resolve, reject) => {
    const client = window.google.accounts.oauth2.initCodeClient({
      client_id: GOOGLE_OAUTH_CLIENT_ID,
      scope: GOOGLE_CODE_FLOW_SCOPE,
      ux_mode: 'popup',
      callback: (response) => {
        if (response && response.code) {
          resolve(response.code)
        } else {
          reject(new Error((response && response.error) || 'No authorization code returned'))
        }
      },
      error_callback: (error) => {
        const err = new Error((error && (error.message || error.type)) || 'Google sign-in failed')
        err.popupClosed = Boolean(error && error.type === 'popup_closed')
        reject(err)
      },
    })
    client.requestCode()
  }))
}

// The trash icon on the connect button signs the BROWSER out; the server-side
// account (and Wins monitoring) is kept and managed from the Settings tab.
// This flag makes that choice stick across reloads until Connect is clicked.
const GOOGLE_SIGNED_OUT_KEY = 'googleBrowserSignedOut'

export const setGoogleBrowserSignedOut = (value) => {
  try {
    if (value) {
      localStorage.setItem(GOOGLE_SIGNED_OUT_KEY, '1')
    } else {
      localStorage.removeItem(GOOGLE_SIGNED_OUT_KEY)
    }
  } catch (e) {
    // storage unavailable - the preference just won't persist
  }
}

const isGoogleBrowserSignedOut = () => {
  try {
    return localStorage.getItem(GOOGLE_SIGNED_OUT_KEY) === '1'
  } catch (e) {
    return false
  }
}

/**
 * Restore a Google session from the backend-held refresh token: fetches a
 * short-lived access token, injects it into gapi.client (all webmasters
 * queries ride this ambient token) and mirrors the account into the store.
 * Returns the plain user ({ name, email, profileImg }) or null.
 * Respects the browser sign-out preference unless options.force is set
 * (an explicit Connect click).
 */
export const restoreBackendGoogleSession = async (store, options = {}) => {
  try {
    if (!options.force && isGoogleBrowserSignedOut()) return null
    const response = await store.dispatch('getGoogleAccessToken')
    const data = response && response.data
    if (!data || data.status !== 'SUCCESS' || !data.accessToken) return null

    backendAccessToken = data.accessToken
    await loadGAPIClient()

    const user = {
      name: data.googleName || data.googleEmail,
      email: data.googleEmail,
      profileImg: null,
    }
    store.commit('updateGoogleUser', user)
    return user
  } catch (e) {
    return null
  }
}


// Access token from the backend-held refresh token (restoreBackendGoogleSession).
// Kept module-level so loadGAPIClient can re-apply it: gapi client setup used
// to reset the ambient token, sending requests with only the API key (401).
let backendAccessToken = null

export const loadGAPIClient = function () {
  return new Promise((resolve) => {
    gapi.load('client', async () => {
      if (!gapi.client) return resolve()
      gapi.client.setApiKey(GOOGLE_API_KEY)
      try {
        await gapi.client.load('https://content.googleapis.com/discovery/v1/apis/searchconsole/v1/rest')
        console.log('GAPI client loaded for API')
      } catch (err) {
        // already loaded is fine
      }
      if (backendAccessToken) {
        gapi.client.setToken({ access_token: backendAccessToken })
      }
      resolve()
    })
  })
}



function getOriginFromUrl(url) {
  try {
    const { origin } = new URL(url);
    return origin
  } catch (e) {
    console.log('getDomain', e)
    return ''
  }
}
export const getCountryCode_ISO3 = (location_code) => {
  const loc = locations.find(l => l.location_code == location_code)
  if (loc) {
    const country = getCountryData(loc.country_iso_code)
    if (country) {
      return country.iso3
    }
  }
  return 'USA'
}


const VOLTAGE_LEVELS = [
  0, 10, 20, 50, 100, 200, 500, 1000, 1500, 2000, 3000, 4000, 5000, 7500, 10000, 12500, 15000, 25000
]
function calculateVoltageLevelWithImpressions(rows) {
  let minImpressions = 99999;
  let maxImpressions = 0;
  for (let i = 0; i < rows.length; i++) {
    let impressions = rows[i].impressions
    if (impressions > maxImpressions) maxImpressions = impressions
    if (impressions < minImpressions) minImpressions = impressions
  }
  let average = (minImpressions + maxImpressions) / 2
  for (let i = 0; i < VOLTAGE_LEVELS.length - 1; i++) {
    if (VOLTAGE_LEVELS[i] < average && VOLTAGE_LEVELS[i + 1] >= average) {
      return {
        average: average,
        levelMin: VOLTAGE_LEVELS[i],
        levelMax: VOLTAGE_LEVELS[i + 1],
      }
    }
  }
  return 0
}

const makeNotContainKeywordFilter = (keywords) => {
  let notContainRestriction = []
  keywords.map((keyword) => {
    notContainRestriction.push(
      {
        "dimension": "QUERY",
        "operator": "NOT_CONTAINS",
        "expression": keyword
      }
    )
  })
  return notContainRestriction
}

export const calcCannibalization = (average_impressions, current_impressions) => {
  const minimum_impressions = average_impressions * 0.25
  if (current_impressions >= average_impressions) {
    return 'high'
  } else if (current_impressions < average_impressions && current_impressions >= minimum_impressions) {
    return 'medium'
  } else {
    return 'low'
  }
}

export const calculateVoltageLevel = async (targetUrl, location, brandedKeywords) => {
  try {

    let locationCode = getCountryCode_ISO3(location)

    let notContainRestriction = makeNotContainKeywordFilter(brandedKeywords)


    return new Promise((resolve, reject) => {
      gapi.client.webmasters.searchanalytics.query({
        "siteUrl": getOriginFromUrl(targetUrl),
        "resource": {
          "startDate": moment().subtract(3, 'month').format('YYYY-MM-DD'),
          "endDate": moment().format('YYYY-MM-DD'),
          "dimensions": [
            "DATE"
          ],
          "rowLimit": 25000,
          "dimensionFilterGroups": [
            {
              "filters": [
                {
                  "dimension": "COUNTRY",
                  "expression": locationCode
                },
                ...notContainRestriction
              ]
            }
          ]
        }
      })
        .then(
          function (response) {
            // Handle the results here (response.result has the parsed body).

            let voltageLevel = calculateVoltageLevelWithImpressions(response?.result?.rows || [])
            resolve(voltageLevel)
          },
          function (err) {
            console.error("Execute error", err);
            reject(err);
          }
        );
    })

  } catch (e) {
    console.log(e);
    throw new Error('Failed')
  }
}

export const getSitesList = () => {
  return gapi.client?.webmasters?.sites?.list({})
}
export const searchAnalyticsForSearchConsole = (targetUrl, location, brandedKeywords, dateRanges) => {
  try {

    if (!targetUrl.endsWith('/')) {
      targetUrl += '/'
    }

    let locationCode = getCountryCode_ISO3(location)
    let notContainRestriction = makeNotContainKeywordFilter(brandedKeywords)

    let targetPageFilter = []
    targetPageFilter.push({
      "dimension": "PAGE",
      "operator": "EQUALS",
      "expression": targetUrl
    })

    return new Promise((resolve, reject) => {
      gapi.client.webmasters.searchanalytics.query({
        "siteUrl": getOriginFromUrl(targetUrl),
        "resource": {
          "startDate": dateRanges.startDate.format('YYYY-MM-DD'),
          "endDate": dateRanges.endDate.format('YYYY-MM-DD'),
          "dimensions": [
            "QUERY"
          ],
          "rowLimit": 25000,
          "dimensionFilterGroups": [
            {
              "filters": [
                {
                  "dimension": "COUNTRY",
                  "expression": locationCode
                },
                ...notContainRestriction,
                ...targetPageFilter,
              ]
            }
          ]
        }
      })
        .then(
          function (response) {
            // Handle the results here (response.result has the parsed body).

            let result = response?.result?.rows || []
            resolve(result)
          },
          function (err) {
            console.error("Execute error", err);
            reject(err);
          }
        );
    })

  } catch (e) {
    console.log(e);
    throw new Error('Failed')
  }
}


export const searchAnalyticsForSearchConsole_byPage = (targetUrl, location, dateRanges) => {
  try {

    let locationCode = getCountryCode_ISO3(location)
    // let brandedKeywords = []
    // let notContainRestriction = makeNotContainKeywordFilter(brandedKeywords)

    return new Promise((resolve, reject) => {
      gapi.client.webmasters.searchanalytics.query({
        "siteUrl": getOriginFromUrl(targetUrl),
        "resource": {
          "startDate": dateRanges.startDate.format('YYYY-MM-DD'),
          "endDate": dateRanges.endDate.format('YYYY-MM-DD'),
          "dimensions": [
            "PAGE",
            "QUERY"
          ],
          "rowLimit": 25000,
          "dimensionFilterGroups": [
            {
              "filters": [
                {
                  "dimension": "COUNTRY",
                  "expression": locationCode
                },
              ]
            }
          ]
        }
      })
        .then(
          function (response) {
            // Handle the results here (response.result has the parsed body).

            let result = response?.result?.rows || []
            console.log("result", result)

            result = result.map(r => {
              if (r.keys.length == 2) {
                r['url'] = r.keys[0]
                r['keyword'] = r.keys[1]
                delete r.keys
              }
              return r
            })

            resolve(result)
          },
          function (err) {
            console.error("Execute error", err);
            reject(err);
          }
        );
    })

  } catch (e) {
    console.log(e);
    throw new Error('Failed')
  }
}